import { RefObject, useEffect, useRef, useState } from "react"

const getLineClamp = (el: HTMLElement) => {
  const style = window.getComputedStyle(el)
  const clampRaw =
    (style as { webkitLineClamp?: string; lineClamp?: string }).webkitLineClamp ||
    (style as { lineClamp?: string }).lineClamp
  const clamp = clampRaw ? parseInt(clampRaw, 10) : NaN
  return Number.isFinite(clamp) && clamp > 0 ? clamp : undefined
}

const isElementOverflowing = (el: HTMLElement) => {
  if (el.clientHeight === 0 && el.clientWidth === 0) return false
  return el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1
}

const measureOverflow = (element: HTMLElement) => {
  const child = element.firstElementChild as HTMLElement | null
  const childHasClamp = child ? getLineClamp(child) !== undefined : false
  let overflowing = child && childHasClamp ? isElementOverflowing(child) : isElementOverflowing(element)
  if (!overflowing && child && !childHasClamp) {
    overflowing = isElementOverflowing(child)
  }
  return overflowing
}

/**
 * 检查 DOM 元素的内容是否发生溢出
 * 并使用 ResizeObserver 监听元素尺寸变化以重新检查
 * @returns [isOverflowing, ref]
 * - isOverflowing: boolean 文本是否溢出
 * - ref: React.RefObject<T | null> 需要绑定的 DOM 引用
 */
const useIsOverflow = <T extends HTMLElement = HTMLDivElement>(): [boolean, RefObject<T | null>] => {
  const ref = useRef<T>(null)
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const checkOverflow = () => {
      const overflowing = measureOverflow(element)
      setIsOverflowing((current) => (current === overflowing ? current : overflowing))
    }

    const resizeObserver = new ResizeObserver(checkOverflow)
    let observedChild: Element | null = null
    const observeCurrentChild = () => {
      const currentChild = element.firstElementChild
      if (currentChild === observedChild) return
      if (observedChild) resizeObserver.unobserve(observedChild)
      observedChild = currentChild
      if (observedChild) resizeObserver.observe(observedChild)
    }

    resizeObserver.observe(element)
    observeCurrentChild()

    // 文本、子节点或样式变化由 MutationObserver 驱动，避免父组件每次渲染都强制测量布局。
    const mutationObserver = new MutationObserver(() => {
      observeCurrentChild()
      checkOverflow()
    })
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    })

    checkOverflow()

    return () => {
      mutationObserver.disconnect()
      resizeObserver.unobserve(element)
      if (observedChild) {
        resizeObserver.unobserve(observedChild)
      }
      resizeObserver.disconnect()
    }
  }, [])

  return [isOverflowing, ref]
}

export default useIsOverflow
